import { Icon } from '@progress/kendo-react-common';
import {
  TreeView,
  TreeViewCheckChangeEvent,
  TreeViewExpandChangeEvent,
} from '@progress/kendo-react-treeview';
import { FC, KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Keys } from 'src/constants/keys';
import { TreeViewItem } from 'src/types/TreeView';
import { stopPropagation } from 'src/utils';
import Button from './Button';
import SearchBox from './SearchBox';

interface TreeViewCheckboxProps {
  data: TreeViewItem[];
  placeholder?: string;
  width?: string;
  onChange?: (checkedItems: string[]) => void;
  label?: string;
  checkField?: string;
  expandField?: string;
  textField?: string;
  checkIndeterminateField?: string;
}

const TreeViewCheckbox: FC<TreeViewCheckboxProps> = ({
  data,
  placeholder = 'Select items',
  width = '250px',
  onChange,
  label,
  checkField = 'checked',
  expandField = 'expanded',
  textField = 'text',
  checkIndeterminateField = 'indeterminate',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [treeData, setTreeData] = useState<TreeViewItem[]>([]);
  const [initialTreeData, setInitialTreeData] = useState<TreeViewItem[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const pendingDataRef = useRef<TreeViewItem[] | null>(null);
  const { t } = useTranslation();
  // Helper function to deep clone and initialize tree data
  const initializeTreeData = useCallback(
    (items: TreeViewItem[]): TreeViewItem[] =>
      items.map((item) => {
        const processedItem = { ...item };

        if (processedItem.items && processedItem.items.length > 0) {
          if (processedItem.expanded === undefined) {
            processedItem.expanded = true;
          }
          processedItem.items = initializeTreeData(processedItem.items);
        }

        return processedItem;
      }),
    []
  );

  // Deep clone function - using structuredClone for better performance and accuracy
  const deepClone = useCallback((items: TreeViewItem[]): TreeViewItem[] => {
    // Use structuredClone if available (modern browsers), fallback to manual clone
    if (typeof structuredClone !== 'undefined') {
      return structuredClone(items);
    }
    // Fallback: manual deep clone (more reliable than JSON.parse/stringify)
    return JSON.parse(JSON.stringify(items));
  }, []);

  // Helper: Count only leaf items (items without children)
  const countLeafItems = useCallback(
    (items: TreeViewItem[]): { total: number; checked: number } => {
      let total = 0;
      let checked = 0;

      const traverse = (nodes: TreeViewItem[]) => {
        nodes.forEach((node) => {
          // Skip "All" item from the count
          if (node.isSelectAll) {
            return;
          }

          // Only count LEAF items (items without children)
          if (node.items && node.items.length > 0) {
            // This is a parent/container, don't count it, just traverse children
            traverse(node.items);
          } else {
            // This is a leaf item
            total++;
            if (node.checked) {
              checked++;
            }
          }
        });
      };

      traverse(items);
      return { total, checked };
    },
    []
  );

  const getCheckedItems = useCallback((items: TreeViewItem[]): string[] => {
    const checked: string[] = [];
    const traverse = (nodes: TreeViewItem[]) => {
      nodes.forEach((node) => {
        // Skip "All" item from the count
        if (node.isSelectAll) {
          return;
        }

        // Only count LEAF items (items without children)
        // Parent items are just containers/placeholders
        if (node.items && node.items.length > 0) {
          // This is a parent/container, don't count it, just traverse children
          traverse(node.items);
        } else {
          // This is a leaf item, count it if checked - return text value
          if (node.checked) {
            checked.push(node.id);
          }
        }
      });
    };
    traverse(items);
    return checked;
  }, []);

  // Helper: Calculate checked and indeterminate state based on counts
  const calculateCheckState = useCallback(
    (checkedCount: number, indeterminateCount: number, total: number) => {
      if (checkedCount === total && total > 0) {
        return { checked: true, indeterminate: false };
      }
      if (checkedCount > 0 || indeterminateCount > 0) {
        return { checked: false, indeterminate: true };
      }
      return { checked: false, indeterminate: false };
    },
    []
  );

  // Calculate indeterminate states for parent nodes and "All" item
  const updateIndeterminateStates = useCallback(
    (items: TreeViewItem[]): TreeViewItem[] => {
      // First, process regular hierarchical items
      const processedItems = items.map((item) => {
        if (item.isSelectAll) {
          // Skip "All" item for now, we'll handle it after
          return item;
        }

        if (item.items && item.items.length > 0) {
          // Process children first (recursive)
          const processedChildren = item.items.map((child) => {
            if (child.items && child.items.length > 0) {
              const grandChildren = updateIndeterminateStates(child.items);
              const checkedCount = grandChildren.filter((gc) => gc.checked).length;
              const indeterminateCount = grandChildren.filter((gc) => gc.indeterminate).length;
              const totalGrandChildren = grandChildren.length;

              const checkState = calculateCheckState(
                checkedCount,
                indeterminateCount,
                totalGrandChildren
              );

              return {
                ...child,
                items: grandChildren,
                checked: checkState.checked,
                indeterminate: checkState.indeterminate,
              };
            }
            return { ...child, indeterminate: false };
          });

          // Count checked and indeterminate children
          const checkedCount = processedChildren.filter((child) => child.checked).length;
          const indeterminateCount = processedChildren.filter(
            (child) => child.indeterminate
          ).length;
          const totalChildren = processedChildren.length;

          const checkState = calculateCheckState(checkedCount, indeterminateCount, totalChildren);

          return {
            ...item,
            items: processedChildren,
            checked: checkState.checked,
            indeterminate: checkState.indeterminate,
          };
        }

        // Leaf node
        return {
          ...item,
          indeterminate: false,
        };
      });

      // Now handle "All" item - check the state of all other items (only leaf items)
      return processedItems.map((item) => {
        if (item.isSelectAll) {
          // Get all other items (excluding "All")
          const otherItems = processedItems.filter((i) => !i.isSelectAll);

          // Use consistent leaf-only counting
          const { total, checked } = countLeafItems(otherItems);

          const checkState = calculateCheckState(checked, 0, total);

          return {
            ...item,
            checked: checkState.checked,
            indeterminate: checkState.indeterminate,
          };
        }
        return item;
      });
    },
    [calculateCheckState, countLeafItems]
  );

  const toggleDropdown = useCallback(() => {
    if (!isOpen) {
      // Opening: use data prop from context (filter text preserved)
      const initializedData = initializeTreeData(data);
      const dataWithIndeterminate = updateIndeterminateStates(initializedData);
      const clonedData = deepClone(dataWithIndeterminate);
      setTreeData(clonedData);
      setInitialTreeData(deepClone(clonedData));
    }
    setIsOpen((prev) => !prev);
  }, [isOpen, data, initializeTreeData, updateIndeterminateStates, deepClone]);

  const handleClose = useCallback(() => {
    setSearchValue('');
    setIsOpen(false);
    pendingDataRef.current = null;
    setInitialTreeData([]);
  }, []);

  // Helper function to get all category IDs from tree data
  const getAllCategoryIds = useCallback((items: TreeViewItem[]): string[] => {
    const allIds: string[] = [];
    const traverse = (nodes: TreeViewItem[]) => {
      nodes.forEach((node) => {
        // Skip "All" item
        if (node.isSelectAll) {
          return;
        }
        // Add category ID if it matches the pattern
        if (node.id.match(/^category-\d+$/)) {
          allIds.push(node.id);
        }
        // Recursively traverse children
        if (node.items && node.items.length > 0) {
          traverse(node.items);
        }
      });
    };
    traverse(items);
    return allIds;
  }, []);

  const filterTreeData = useCallback((items: TreeViewItem[], search: string): TreeViewItem[] => {
    if (!search) return items;

    const searchLower = search.toLowerCase();
    const filtered: TreeViewItem[] = [];

    items.forEach((item) => {
      const matchesText = item.text.toLowerCase().includes(searchLower);
      const filteredChildren = item.items ? filterTreeData(item.items, search) : [];

      if (matchesText || filteredChildren.length > 0) {
        filtered.push({
          ...item,
          items: filteredChildren.length > 0 ? filteredChildren : item.items,
          expanded: search ? true : item.expanded,
        });
      }
    });

    return filtered;
  }, []);

  const handleApply = useCallback(() => {
    if (!onChange) {
      setIsOpen(false);
      if (pendingDataRef.current) pendingDataRef.current = null;
      return;
    }

    // When user has searched: pass only checked items that appear in the filtered tree.
    // When no search: use full-tree logic (All / none / specific).
    const dataWithIndeterminate = updateIndeterminateStates(treeData);
    let result: string[];

    if (searchValue.trim() !== '') {
      const filtered = filterTreeData(dataWithIndeterminate, searchValue);
      result = getCheckedItems(filtered);
    } else {
      const allItem = dataWithIndeterminate.find((item) => item.isSelectAll);
      const allItemChecked = allItem?.checked ?? false;
      const allItemIndeterminate = allItem?.indeterminate ?? false;

      if (allItemChecked && !allItemIndeterminate) {
        result = getAllCategoryIds(dataWithIndeterminate);
      } else if (!allItemChecked && !allItemIndeterminate) {
        result = [];
      } else {
        result = getCheckedItems(dataWithIndeterminate);
      }
    }

    onChange(result);
    setSearchValue('');
    setIsOpen(false);
    if (pendingDataRef.current) pendingDataRef.current = null;
  }, [
    treeData,
    searchValue,
    updateIndeterminateStates,
    getCheckedItems,
    getAllCategoryIds,
    filterTreeData,
    onChange,
  ]);

  // Track pending data changes when dropdown is open
  useEffect(() => {
    if (isOpen) {
      // Dropdown is open, store pending data to apply when closed
      pendingDataRef.current = data;
    } else {
      // Dropdown is closed, clear pending data
      pendingDataRef.current = null;
    }
  }, [data, isOpen]);

  // Tab key press handler to trap focus within popup
  const handleTabKeyPress = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    // when user press tab key trap focus
    const popupElement = document.getElementsByClassName('treeViewCheckboxPopup')[0] as HTMLElement;
    if (!popupElement) return;
    const focusableElements = popupElement.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    ) as NodeListOf<HTMLElement>;
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.code === Keys.tab) {
        handleTabKeyPress(event);
      } else if (event.key === Keys.esc) {
        handleClose();
      }
    },
    [handleClose, handleTabKeyPress]
  );

  // Close dropdown when clicking outside or pressing ESC
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };

    const handleEscKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, handleClose]);

  const updateCheckedState = useCallback(
    (items: TreeViewItem[], id: string, checked: boolean): TreeViewItem[] =>
      items.map((item) => {
        if (item.id === id) {
          const updatedItem = { ...item, checked, indeterminate: false };
          // Update all children recursively
          if (updatedItem.items && updatedItem.items.length > 0) {
            const updateAllChildren = (children: TreeViewItem[]): TreeViewItem[] =>
              children.map((child) => ({
                ...child,
                checked,
                indeterminate: false,
                items: child.items ? updateAllChildren(child.items) : undefined,
              }));
            updatedItem.items = updateAllChildren(updatedItem.items);
          }
          return updatedItem;
        }
        if (item.items) {
          return { ...item, items: updateCheckedState(item.items, id, checked) };
        }
        return item;
      }),
    []
  );

  // Update all items when "All" is clicked
  const updateAllItemsCheckedState = useCallback(
    (items: TreeViewItem[], checked: boolean): TreeViewItem[] =>
      items.map((item) => {
        if (item.isSelectAll) {
          // Just update "All" item itself
          return { ...item, checked, indeterminate: false };
        }

        // Update all other items
        const updatedItem = { ...item, checked, indeterminate: false };
        if (updatedItem.items && updatedItem.items.length > 0) {
          const updateChildren = (children: TreeViewItem[]): TreeViewItem[] =>
            children.map((child) => ({
              ...child,
              checked,
              indeterminate: false,
              items: child.items ? updateChildren(child.items) : undefined,
            }));
          updatedItem.items = updateChildren(updatedItem.items);
        }
        return updatedItem;
      }),
    []
  );

  // Uncheck all items when "All" is unchecked
  const uncheckAllItems = useCallback(
    (items: TreeViewItem[]): TreeViewItem[] =>
      items.map((item) => {
        if (item.isSelectAll) {
          // Uncheck "All" item itself
          return { ...item, checked: false, indeterminate: false };
        }

        // Uncheck all other items recursively
        const updatedItem = { ...item, checked: false, indeterminate: false };
        if (updatedItem.items && updatedItem.items.length > 0) {
          const uncheckChildren = (children: TreeViewItem[]): TreeViewItem[] =>
            children.map((child) => ({
              ...child,
              checked: false,
              indeterminate: false,
              items: child.items ? uncheckChildren(child.items) : undefined,
            }));
          updatedItem.items = uncheckChildren(updatedItem.items);
        }
        return updatedItem;
      }),
    []
  );

  const onCheckChange = useCallback(
    (event: TreeViewCheckChangeEvent) => {
      const itemId = event.item.id;
      const currentChecked = event.item.checked ?? false;
      const currentIndeterminate = event.item.indeterminate ?? false;
      const isSelectAllItem = event.item.isSelectAll ?? false;

      // If indeterminate, next state should be checked
      // If checked, next state should be unchecked
      // If unchecked, next state should be checked
      const newCheckedState = currentIndeterminate || !currentChecked;

      let updatedData;

      if (isSelectAllItem) {
        // Clicking on "All" - select/deselect everything
        if (newCheckedState) {
          // Check all items
          updatedData = updateAllItemsCheckedState(treeData, true);
        } else {
          // Uncheck all items
          updatedData = uncheckAllItems(treeData);
        }
      } else {
        // Regular item click
        updatedData = updateCheckedState(treeData, itemId, newCheckedState);
      }

      // Update indeterminate states for all parent nodes and "All" item
      updatedData = updateIndeterminateStates(updatedData);

      setTreeData(updatedData);

      // Don't call onChange here - only call it when Apply is clicked
    },
    [
      treeData,
      updateCheckedState,
      updateAllItemsCheckedState,
      uncheckAllItems,
      updateIndeterminateStates,
    ]
  );

  // Helper function to recursively toggle expanded state of an item and all its children
  const toggleItemExpandedState = useCallback(
    (items: TreeViewItem[], targetId: string, shouldExpand?: boolean): TreeViewItem[] =>
      items.map((item) => {
        if (item.id === targetId) {
          const newExpanded = shouldExpand !== undefined ? shouldExpand : !item.expanded;
          // Recursively expand/collapse all children
          const updatedItems = item.items
            ? item.items.map((child) => ({
                ...child,
                expanded: newExpanded,
                items: child.items
                  ? toggleItemExpandedState([child], child.id, newExpanded)[0].items
                  : undefined,
              }))
            : undefined;
          return {
            ...item,
            expanded: newExpanded,
            items: updatedItems,
          };
        }
        if (item.items) {
          return {
            ...item,
            items: toggleItemExpandedState(item.items, targetId, shouldExpand),
          };
        }
        return item;
      }),
    []
  );

  const onExpandChange = useCallback(
    (event: TreeViewExpandChangeEvent) => {
      const updateExpandedState = (items: TreeViewItem[], id: string): TreeViewItem[] =>
        items.map((item) => {
          if (item.id === id) {
            return { ...item, expanded: !item.expanded };
          }
          if (item.items) {
            return { ...item, items: updateExpandedState(item.items, id) };
          }
          return item;
        });

      setTreeData(updateExpandedState(treeData, event.item.id));
    },
    [treeData]
  );

  // Handle item click - toggle expand/collapse when clicking on parent item text
  const handleItemClick = useCallback(
    (event: any) => {
      const item = event.item as TreeViewItem;

      // Only toggle expand/collapse if item has children
      // Skip "All Categories" item (isSelectAll) as it has special checkbox behavior
      if (item.items && item.items.length > 0 && !item.isSelectAll) {
        setTreeData((prevData) => toggleItemExpandedState(prevData, item.id));
      }
    },
    [toggleItemExpandedState]
  );

  const handleSearchChange = useCallback((newSearchValue: string) => {
    setSearchValue(newSearchValue);
  }, []);

  // Memoize filtered data to avoid recalculating on every render
  const filteredData = useMemo(
    () => filterTreeData(treeData, searchValue),
    [treeData, searchValue, filterTreeData]
  );

  // Custom item renderer to add data attribute for "All Categories"
  const itemRender = useCallback((props: any) => {
    const item = props.item as TreeViewItem;
    const isSelectAll = item.isSelectAll || false;
    // Only show tooltip for leaf items (items without children) and not for "All Categories"
    const isLeafItem = !item.items || item.items.length === 0;
    const shouldShowTooltip = item.text && item.text.length > 20 && isLeafItem && !isSelectAll;

    return (
      <span
        data-select-all={isSelectAll}
        {...(shouldShowTooltip
          ? {
              'data-tooltip-id': 'global-tooltip',
              'data-tooltip-content': item.text,
            }
          : {})}
        style={{
          fontSize: '13px',
          color: '#333',
        }}
      >
        {item.text}
      </span>
    );
  }, []);

  // Calculate display text based on applied selection state
  const getDisplayText = useCallback(() => {
    // Use data prop directly (has persisted state from context), not appliedData
    const displayData = data.length > 0 ? data : [];

    // Find the "All Categories" item
    const allItem = displayData.find((item) => item.isSelectAll);

    // If "All Categories" is checked and not indeterminate, display its text
    if (allItem && allItem.checked && !allItem.indeterminate) {
      return allItem.text || 'All Categories';
    }

    // Otherwise, count selected items
    const selectedCount = getCheckedItems(displayData).length;

    if (selectedCount > 0) {
      return `${selectedCount} ${t('selected')}`;
    } else if (selectedCount === 0) {
      return t('No items selected');
    }

    return placeholder;
  }, [data, getCheckedItems, placeholder, t]);

  const displayText = getDisplayText();

  // Helper function to compare two tree data structures for changes
  const compareTreeData = useCallback(
    (current: TreeViewItem[], initial: TreeViewItem[]): boolean => {
      if (current.length !== initial.length) return true;

      const traverse = (curr: TreeViewItem[], init: TreeViewItem[]): boolean => {
        for (let i = 0; i < curr.length; i++) {
          const currItem = curr[i];
          const initItem = init.find((item) => item.id === currItem.id);

          if (!initItem) return true;
          if (currItem.checked !== initItem.checked) return true;
          if (currItem.indeterminate !== initItem.indeterminate) return true;

          if (currItem.items && initItem.items) {
            if (traverse(currItem.items, initItem.items)) return true;
          } else if (currItem.items !== initItem.items) {
            return true;
          }
        }
        return false;
      };

      return traverse(current, initial);
    },
    []
  );

  // Check if current state matches initial state (no changes made)
  const hasChanges = useMemo(() => {
    if (initialTreeData.length === 0) return false;
    return compareTreeData(treeData, initialTreeData);
  }, [treeData, initialTreeData, compareTreeData]);

  // Checked leaf count in the current filtered view (for Apply button and API payload)
  const checkedInFilteredCount = useMemo(
    () => getCheckedItems(filteredData).length,
    [filteredData, getCheckedItems]
  );
  // Determine if Apply button should be disabled
  const isApplyDisabled = useMemo(() => {
    if (filteredData.length === 0) return true;
    // Disable when no item is selected in the (filtered) list
    if (checkedInFilteredCount === 0) return true;
    // Enable when there are changes or at least one checked in filtered list
    if (hasChanges || checkedInFilteredCount > 0) return false;
    return !hasChanges;
  }, [filteredData.length, hasChanges, checkedInFilteredCount]);

  const footer = useMemo(
    () => (
      <div onClick={stopPropagation} className="flex item-center justify-end gap-1 p-2">
        <Button onClick={handleApply} size="md" color="primary" disabled={isApplyDisabled}>
          {t('Apply')}
        </Button>
        <Button onClick={handleClose} size="md" color="error">
          {t('Cancel')}
        </Button>
      </div>
    ),
    [handleApply, handleClose, isApplyDisabled, t]
  );

  return (
    <div ref={dropdownRef} className="relative tree-view-dropdown" style={{ width }}>
      {label && <label className="text-xs mb-1 block">{label}</label>}

      {/* Dropdown Toggle - Matching Kendo UI DropDownList Style */}
      <button
        type="button"
        className="k-input k-input-md k-rounded-md k-input-solid k-picker k-picker-md k-picker-solid k-dropdownlist w-full"
        onClick={toggleDropdown}
        style={{
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span
          className="k-input-inner"
          style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          <span
            className="k-input-value-text"
            {...(displayText && displayText.length > 20
              ? {
                  'data-tooltip-id': 'global-tooltip',
                  'data-tooltip-content': displayText,
                }
              : {})}
          >
            {displayText}
          </span>
        </span>
        <span className="k-input-button k-button k-button-md k-rounded-md k-button-solid k-button-solid-base k-icon-button">
          <span className="k-button-icon k-icon">
            <Icon name={isOpen ? 'caret-alt-up' : 'caret-alt-down'} />
          </span>
        </span>
      </button>

      {/* Dropdown Content */}
      {isOpen && (
        <div
          className="k-animation-container k-animation-container-shown absolute inline-dropdown-overlay mt-1"
          style={{ width: '100%' }}
        >
          <div
            className="k-popup k-group k-reset k-list-container treeViewCheckboxPopup"
            style={{
              // Fixed cap so the popup doesn't shrink on taller screens; still fits short viewports
              maxHeight: 'min(450px, calc(100vh - 200px))',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
            onKeyDown={handleKeyDown}
          >
            {/* Search Box */}
            <div className="p-2 border-b border-gray-200 flex-shrink-0">
              <SearchBox
                placeholder={t('Search')}
                width="100%"
                value={searchValue}
                onChange={handleSearchChange}
              />
            </div>

            {/* TreeView */}
            <div
              style={{
                flex: '1 1 0%',
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'auto',
              }}
            >
              <div className="w-fit min-w-full">
                {filteredData.length > 0 ? (
                  <TreeView
                    data={filteredData}
                    expandIcons={true}
                    onExpandChange={onExpandChange}
                    onCheckChange={onCheckChange}
                    onItemClick={handleItemClick}
                    checkboxes={true}
                    textField={textField}
                    checkField={checkField}
                    expandField={expandField}
                    checkIndeterminateField={checkIndeterminateField}
                    item={itemRender}
                  />
                ) : (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    <Icon name="warning" className="text-2xl mb-2" />
                    <p>{t('NO DATA FOUND.')}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer with Apply and Cancel buttons */}
            <div className="flex-shrink-0">{footer}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TreeViewCheckbox;
